import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllTestimonials } from "../redux/actions/contents";
import useAuth from "../useAuth";
import PageName from "../Components/global/PageName";
import { Rating } from "@mui/material";
import { FaCheckCircle, FaTimesCircle, FaQuoteLeft, FaPhoneAlt, FaTrash } from "react-icons/fa";
import { GiSparkles } from "react-icons/gi";

export default function TestimonialManager() {
  const dispatch = useDispatch();
  const { testimonialsList, loading: testimonialsLoading } = useSelector(
    (state) => state.content
  );
  const { clientInfo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllTestimonials(1, 100, clientInfo?.clientId));
  }, [sync, dispatch, clientInfo]);

  const handleUpdateVerifyStatus = async (id, verifiedStatus) => {
    const userRes = window.confirm(
      `Are you sure you want to ${
        verifiedStatus ? "Un-Verify" : "Verify"
      } this review?`
    );
    if (!userRes) return null;

    try {
      setLoading(true);
      await axios.post(
        `${server}/testimonial/updateVerifyStatus`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            testimonialId: id,
            verifyStatus: !verifiedStatus,
          },
        }
      );
      toast.success(verifiedStatus ? "Review un-verified" : "Review verified");
      setSync((prv) => prv + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this client testimonial review?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data: res } = await axios.delete(
        `${server}/testimonial/deleteById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            testimonialId: id,
            id: id,
          },
        }
      );
      toast.success(res?.message || "Testimonial review deleted.");
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Delete operation failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Testimonials & Reviews Manager"
        subTitle="Verify, manage and feature client reviews on the main site in dark mode."
      />

      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="overflow-x-auto rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.7)] border border-[#C8A165]/25 bg-[#121215]/90 backdrop-blur-2xl">
          <table className="min-w-full divide-y divide-[#C8A165]/15 text-xs">
            <thead className="bg-[#18181D] text-[#C8A165] uppercase tracking-widest font-bold">
              <tr>
                <th className="px-6 py-4 text-left">Client Avatar</th>
                <th className="px-6 py-4 text-left">Client Name & Mobile</th>
                <th className="px-6 py-4 text-left">Review Quote</th>
                <th className="px-6 py-4 text-center">Rating</th>
                <th className="px-6 py-4 text-center">Verification Status</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#C8A165]/10 text-left">
              {testimonialsList?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-16 text-[#F8F5F2]/40">
                    <GiSparkles size={24} className="mx-auto mb-2 text-[#C8A165]" />
                    No client reviews found.
                  </td>
                </tr>
              ) : (
                testimonialsList?.map((item, index) => (
                  <motion.tr
                    key={item?.id || index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    className="hover:bg-white/[0.03] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="w-10 h-10 rounded-xl bg-[#C8A165]/15 border border-[#C8A165]/30 text-[#C8A165] flex items-center justify-center font-bold text-sm">
                        {item?.name?.[0]?.toUpperCase() || "C"}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-[#F8F5F2]">
                      <div>{item?.name || "Anonymous Client"}</div>
                      <div className="flex items-center gap-1.5 text-[#F8F5F2]/50 font-normal mt-0.5 text-[0.7rem]">
                        <FaPhoneAlt size={10} className="text-[#C8A165]" />
                        <span>{item?.mobile || "N/A"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#F8F5F2]/80 max-w-md">
                      <div className="flex items-start gap-2">
                        <FaQuoteLeft className="text-[#C8A165]/50 flex-shrink-0 mt-0.5" size={13} />
                        <p className="italic text-[#F8F5F2]/90 leading-relaxed">
                          "{item?.review || "No review content provided."}"
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-block bg-[#18181D] px-2.5 py-1 rounded-xl border border-[#C8A165]/20">
                        <Rating value={item?.rating || 5} readOnly size="small" sx={{ color: "#C8A165" }} />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center font-semibold">
                      {item?.verified ? (
                        <span className="inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs font-bold px-3 py-1 rounded-full">
                          <FaCheckCircle size={11} /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-rose-500/15 text-rose-400 border border-rose-500/30 text-xs font-bold px-3 py-1 rounded-full">
                          <FaTimesCircle size={11} /> Un-Verified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            handleUpdateVerifyStatus(item?.id, item?.verified)
                          }
                          className={`text-xs px-3.5 py-1.5 rounded-xl font-bold tracking-wider uppercase transition-all shadow-md cursor-pointer ${
                            item?.verified
                              ? "bg-amber-500/20 hover:bg-amber-600 text-amber-300 hover:text-[#0A0A0C] border border-amber-500/40"
                              : "bg-emerald-500/20 hover:bg-emerald-600 text-emerald-300 hover:text-[#0A0A0C] border border-emerald-500/40"
                          }`}
                        >
                          {!item?.verified ? "Verify" : "Un-Verify"}
                        </button>

                        <button
                          onClick={() => handleDeleteTestimonial(item?.id)}
                          className="p-2 rounded-xl text-rose-400 hover:bg-rose-600 hover:text-white border border-rose-500/30 transition-all cursor-pointer"
                          title="Delete Testimonial"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(loading || testimonialsLoading) && <Loading />}
    </div>
  );
}
